import { Component, Input, ChangeDetectionStrategy, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { GroupedPayment } from 'app/shared/interfaces/payment.interface';

/**
 * Parameters for Payment Method Actions Cell Renderer
 */
export interface PaymentMethodActionsCellRendererParams {
  showActions: boolean;
  onOpenHistory: EventEmitter<GroupedPayment>;
  onOpenCreditNote: EventEmitter<GroupedPayment>;
}

/**
 * Payment Method Actions Cell Renderer
 *
 * Displays payment type name with conditional action buttons:
 * - History button (always visible if showActions=true)
 * - Credit note button (conditional based on payment type/method codes)
 *
 * Action button visibility rules:
 * - History: showActions === true
 * - Credit Note: showActions === true AND (TP0006 OR TP00011 OR MP00002)
 *
 * @example
 * ```typescript
 * {
 *   colId: 'paymentMethod',
 *   headerName: 'Tipo Pago',
 *   cellRenderer: PaymentMethodActionsCellRendererComponent,
 *   cellRendererParams: {
 *     showActions: true,
 *     onOpenHistory: this.onOpenHistory,
 *     onOpenCreditNote: this.onOpenCreditNote
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-payment-method-actions-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="flex items-center gap-2">
      <!-- Payment Type Name -->
      <span class="uppercase">{{ paymentTypeName }}</span>

      <!-- Action Buttons (if enabled) -->
      <ng-container *ngIf="customParams?.showActions">
        <!-- History Button (Always visible when showActions=true) -->
        <button
          mat-icon-button
          class="icon-size-5"
          matTooltip="Ver historial"
          (click)="onHistoryClick($event)">
          <mat-icon class="icon-size-5">open_in_new</mat-icon>
        </button>

        <!-- Credit Note Button (Conditional visibility) -->
        <button
          *ngIf="showCreditNoteButton"
          mat-icon-button
          class="icon-size-5"
          matTooltip="Ver nota de crédito"
          (click)="onCreditNoteClick($event)">
          <mat-icon class="icon-size-5">visibility</mat-icon>
        </button>
      </ng-container>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentMethodActionsCellRendererComponent implements CellRenderer<GroupedPayment> {
  @Input() params!: CellRendererParams<GroupedPayment>;

  get customParams(): PaymentMethodActionsCellRendererParams | undefined {
    return this.params?.colDef?.cellRendererParams as PaymentMethodActionsCellRendererParams;
  }

  /**
   * Gets the payment type name
   */
  get paymentTypeName(): string {
    return this.params?.data?.paymentType?.name || '-';
  }

  /**
   * Determines if credit note button should be shown
   *
   * Visibility conditions (ANY of these):
   * 1. paymentType.code === 'TP0006' AND has original payments
   * 2. paymentType.code === 'TP00011' AND has original payments
   * 3. originalPayments[0].paymentMethod.code === 'MP00002' AND has original payments
   */
  get showCreditNoteButton(): boolean {
    const data = this.params?.data;
    if (!data || !data.originalPayments || data.originalPayments.length === 0) {
      return false;
    }

    const paymentTypeCode = data.paymentType?.code;
    const firstOriginalPayment = data.originalPayments[0];
    const paymentMethodCode = firstOriginalPayment?.paymentMethod?.code;

    // Check conditions
    const isTP0006 = paymentTypeCode === 'TP0006';
    const isTP00011 = paymentTypeCode === 'TP00011';
    const isMP00002 = paymentMethodCode === 'MP00002';

    return isTP0006 || isTP00011 || isMP00002;
  }

  /**
   * Handles history button click
   */
  onHistoryClick(event: Event): void {
    event.stopPropagation();
    const emitter = this.customParams?.onOpenHistory;
    if (emitter && this.params?.data) {
      emitter.emit(this.params.data);
    }
  }

  /**
   * Handles credit note button click
   */
  onCreditNoteClick(event: Event): void {
    event.stopPropagation();
    const emitter = this.customParams?.onOpenCreditNote;
    if (emitter && this.params?.data) {
      emitter.emit(this.params.data);
    }
  }
}
